import React, { useState } from "react";
import { Box, Typography, TextField, Button, CircularProgress, Radio, RadioGroup, FormControlLabel, FormControl, InputAdornment, IconButton, Avatar } from "@mui/material";
import { Visibility, VisibilityOff, PhotoCamera } from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API_BASE_URL = "https://apiweb.hitpoly.com/ajax/";

const GoogleAuthModal = ({ isLoginProp = true }) => {
  const navigate = useNavigate();
  const { user, loginWithEmail, registerWithEmail, checkUsername, sendVerificationCode, getSuggestions, requestPasswordReset } = useAuth();
  
  // States: 'login_email', 'login_password', 'forgot_password', 'forgot_password_success', 'register_name', 'register_username', 'register_password', 'register_recovery', 'register_verify', 'register_avatar'
  const [step, setStep] = useState(isLoginProp ? 'login_email' : 'register_name');
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectionType, setSelectionType] = useState("suggestion_0"); // 'suggestion_0', 'suggestion_1', or 'custom'
  const [showPassword, setShowPassword] = useState(false);

  // Form Data
  const [formData, setFormData] = useState({
    r_f_n: "", // firstName
    r_l_n: "", // lastName
    hitpoly_username: "",
    password: "",
    recovery_email: "",
    code: "",
    verification_hash: "",
    login_identifier: "", // can be email or hitpoly_username
    login_password: ""
  });

  const [suggestions, setSuggestions] = useState([]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const handleNextStep = async () => {
    setError("");
    setLoading(true);
    try {
      if (step === 'login_email') {
        if (!formData.login_identifier) {
          setError("Introduce tu correo electrónico o username.");
          setLoading(false);
          return;
        }
        setStep('login_password');
      } else if (step === 'login_password') {
        if (!formData.login_password) {
          setError("Introduce tu contraseña.");
          setLoading(false);
          return;
        }
        await loginWithEmail(formData.login_identifier, formData.login_password);
        navigate("/");
      } else if (step === 'register_name') {
        if (!formData.r_f_n || !formData.r_l_n) {
          setError("Introduce tu nombre y apellido.");
          setLoading(false);
          return;
        }
        // Obtener sugerencias al pasar a este paso
        const res = await getSuggestions(formData.r_f_n, formData.r_l_n);
        if (res.status === "success" && res.sugerencias) {
            setSuggestions(res.sugerencias);
            if (res.sugerencias.length > 0) {
                setFormData({ ...formData, hitpoly_username: res.sugerencias[0] });
                setSelectionType("suggestion_0");
            } else {
                setSelectionType("custom");
            }
        }
        setStep('register_username');
      } else if (step === 'register_username') {
        if (!formData.hitpoly_username) {
          setError("Elige un nombre de usuario.");
          setLoading(false);
          return;
        }
        const cleanUsername = formData.hitpoly_username.includes('@hitpoly.com') 
          ? formData.hitpoly_username 
          : `${formData.hitpoly_username}@hitpoly.com`;
        
        const res = await checkUsername(cleanUsername);
        if (res.available) {
          setFormData({ ...formData, hitpoly_username: cleanUsername });
          setStep('register_password');
        } else {
          setError("Este nombre de usuario ya está en uso.");
          setSuggestions(res.sugerencias || []);
        }
      } else if (step === 'register_password') {
        if (!formData.password || formData.password.length < 6) {
          setError("La contraseña debe tener al menos 6 caracteres.");
          setLoading(false);
          return;
        }
        setStep('register_recovery');
      } else if (step === 'register_recovery') {
        if (!formData.recovery_email) {
          setError("Introduce un correo de recuperación.");
          setLoading(false);
          return;
        }
        // Send code
        const res = await sendVerificationCode(formData.recovery_email);
        if (res.status === "success" && res.verification_hash) {
            setFormData({ ...formData, verification_hash: res.verification_hash });
            setStep('register_verify');
        } else {
            setError(res.message || "Error al enviar el código.");
            setLoading(false);
            return;
        }
      } else if (step === 'register_verify') {
        if (!formData.code) {
          setError("Introduce el código de verificación.");
          setLoading(false);
          return;
        }
        await registerWithEmail(
          formData.r_f_n,
          formData.hitpoly_username,
          formData.password,
          formData.recovery_email,
          formData.code,
          formData.verification_hash,
          formData.r_l_n
        );
        setStep('register_avatar');
      } else if (step === 'register_avatar') {
        if (!avatarFile) {
          setError("Debes seleccionar una foto de perfil.");
          setLoading(false);
          return;
        }
        const currentUserId = user?.user_id || user?.id;
        if (!currentUserId) {
          setError("Error de autenticación. Intenta de nuevo.");
          setLoading(false);
          return;
        }
        const authRes = await axios.get(`${API_BASE_URL}imagekit_auth.php?userId=${currentUserId}`);
        const { token, expire, signature, publicKey } = authRes.data;
        const uploadForm = new FormData();
        uploadForm.append("file", avatarFile);
        uploadForm.append("fileName", `avatar_${currentUserId}_${Date.now()}.jpg`);
        uploadForm.append("publicKey", publicKey);
        uploadForm.append("signature", signature);
        uploadForm.append("expire", expire);
        uploadForm.append("token", token);
        uploadForm.append("useUniqueFileName", "true");
        uploadForm.append("folder", `/profiles/${currentUserId}`);
        const resIK = await axios.post("https://upload.imagekit.io/api/v1/files/upload", uploadForm);
        const { url } = resIK.data;
        await axios.post(`${API_BASE_URL}updateSetterController.php`, {
          funcion: "update",
          id: currentUserId,
          avatar: url,
        });
        setStep('register_success');
      } else if (step === 'register_success') {
        navigate("/");
      } else if (step === 'forgot_password') {
        if (!formData.login_identifier) {
          setError("Introduce tu correo o nombre de usuario.");
          setLoading(false);
          return;
        }
        const res = await requestPasswordReset(formData.login_identifier);
        if (res.status === "success") {
          setFormData({ ...formData, masked_email: res.masked_email });
          setStep('forgot_password_success');
        } else {
          setError(res.message || "No pudimos enviar el enlace.");
        }
      } else if (step === 'forgot_password_success') {
        setStep('login_email');
      }
    } catch (err) {
      setError(err.message || "Ocurrió un error inesperado.");
    }
    setLoading(false);
  };

  const handleSwitchMode = () => {
    setStep(step.startsWith('login') ? 'register_name' : 'login_email');
    setError("");
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "450px",
        bgcolor: "#fff",
        borderRadius: "8px",
        p: 4,
        boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        border: "1px solid #e0e0e0"
      }}
    >
      <Box sx={{ mb: 2 }}>
        {/* Usamos el logo de Hitpoly si existe, o texto */}
        <Typography variant="h5" sx={{ fontWeight: "bold", color: "#F21D6B" }}>
          Hitpoly
        </Typography>
      </Box>

      <Typography variant="h5" sx={{ fontWeight: 400, mb: 1, color: "#202124" }}>
        {step.startsWith('login') ? 'Iniciar sesión' : 'Crear tu cuenta de Hitpoly'}
      </Typography>
      
      <Typography variant="body1" sx={{ color: "#202124", mb: 3, textAlign: "center" }}>
        {step === 'login_email' && "Ir a Hitpoly"}
        {step === 'login_password' && formData.login_identifier}
        {step === 'forgot_password' && "Recuperación de cuenta"}
        {step === 'forgot_password_success' && "Revisa tu correo"}
        {step === 'register_name' && "Introduce tu nombre"}
        {step === 'register_username' && "Elige tu dirección de Hitpoly"}
        {step === 'register_password' && "Crea una contraseña segura"}
        {step === 'register_recovery' && "Añade un correo de recuperación"}
        {step === 'register_verify' && `Verifica tu correo: ${formData.recovery_email}`}
        {step === 'register_avatar' && "Sube tu foto de perfil"}
      </Typography>

      <Box sx={{ width: "100%", mb: 3 }}>
        {step === 'login_email' && (
          <TextField
            fullWidth
            label="Correo electrónico o teléfono"
            name="login_identifier"
            value={formData.login_identifier}
            onChange={handleChange}
            variant="outlined"
            error={!!error}
            helperText={error}
          />
        )}
        
        {step === 'login_password' && (
          <Box>
            <TextField
              fullWidth
              type={showPassword ? "text" : "password"}
              label="Introduce tu contraseña"
              name="login_password"
              value={formData.login_password}
              onChange={handleChange}
              variant="outlined"
              error={!!error}
              helperText={error}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ mt: 1, textAlign: "left" }}>
              <Button 
                onClick={() => { setError(""); setStep("forgot_password"); }} 
                sx={{ textTransform: "none", p: 0, minWidth: "auto", color: "#1a73e8", fontWeight: "bold" }}
              >
                ¿Has olvidado tu contraseña?
              </Button>
            </Box>
          </Box>
        )}

        {step === 'forgot_password' && (
          <Box>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Ingresa el correo electrónico o nombre de usuario asociado a tu cuenta para enviarte un enlace seguro de recuperación.
            </Typography>
            <TextField
              fullWidth
              label="Correo electrónico o username"
              name="login_identifier"
              value={formData.login_identifier}
              onChange={handleChange}
              variant="outlined"
              error={!!error}
              helperText={error}
            />
          </Box>
        )}

        {step === 'forgot_password_success' && (
          <Box sx={{ textAlign: 'center', p: 2 }}>
            <Typography variant="body1" sx={{ color: "#202124", mb: 2 }}>
              Te hemos enviado un correo electrónico con un enlace seguro para restablecer tu contraseña a:
            </Typography>
            <Typography variant="h6" sx={{ color: "#1a73e8", fontWeight: "bold", mb: 2 }}>
              {formData.masked_email}
            </Typography>
            <Typography variant="body2" sx={{ color: "#5f6368" }}>
              Revisa tu bandeja de entrada o carpeta de spam.
            </Typography>
          </Box>
        )}

        {step === 'register_name' && (
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <TextField
              fullWidth
              label="Nombre"
              name="r_f_n"
              value={formData.r_f_n}
              onChange={handleChange}
              variant="outlined"
              error={!!error && !formData.r_f_n}
              autoComplete="new-password"
            />
            <TextField
              fullWidth
              label="Apellidos"
              name="r_l_n"
              value={formData.r_l_n}
              onChange={handleChange}
              variant="outlined"
              error={!!error && !formData.r_l_n}
              autoComplete="new-password"
            />
            {error && (
              <Typography variant="caption" color="error">
                {error}
              </Typography>
            )}
          </Box>
        )}

        {step === 'register_username' && (
          <Box>
            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <RadioGroup
                value={selectionType}
                onChange={(e) => {
                    setSelectionType(e.target.value);
                    if (e.target.value === "suggestion_0" && suggestions[0]) {
                        setFormData({ ...formData, hitpoly_username: suggestions[0] });
                    } else if (e.target.value === "suggestion_1" && suggestions[1]) {
                        setFormData({ ...formData, hitpoly_username: suggestions[1] });
                    } else if (e.target.value === "custom") {
                        setFormData({ ...formData, hitpoly_username: "" });
                    }
                    setError("");
                }}
              >
                {suggestions.map((sug, idx) => (
                  <FormControlLabel 
                    key={idx} 
                    value={`suggestion_${idx}`} 
                    control={<Radio color="primary" />} 
                    label={`${sug}@hitpoly.com`} 
                    sx={{ mb: 1 }}
                  />
                ))}
                <FormControlLabel 
                    value="custom" 
                    control={<Radio color="primary" />} 
                    label="Crear dirección de Hitpoly personalizada" 
                />
              </RadioGroup>
            </FormControl>

            {selectionType === "custom" && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Nombre de usuario"
                  name="hitpoly_username"
                  value={formData.hitpoly_username}
                  onChange={handleChange}
                  variant="outlined"
                  error={!!error}
                  helperText={error || "Puedes usar letras, números y puntos"}
                  InputProps={{
                    endAdornment: <Typography sx={{color:"#5f6368"}}>@hitpoly.com</Typography>
                  }}
                  autoComplete="off"
                />
              </Box>
            )}
          </Box>
        )}

        {step === 'register_success' && (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>¡Registro Completado!</Typography>
            <Typography variant="body2" sx={{ mb: 3 }}>
              Guarda tus credenciales en un lugar seguro.
            </Typography>
            <Box sx={{ bgcolor: "#f9f9f9", p: 2, borderRadius: 1, border: "1px solid #ddd", mb: 2 }}>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold' }}>Usuario</Typography>
              <Typography sx={{ fontFamily: 'monospace', mb: 1 }}>{formData.hitpoly_username}</Typography>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold' }}>Contraseña</Typography>
              <Typography sx={{ fontFamily: 'monospace' }}>{formData.password}</Typography>
            </Box>
          </Box>
        )}

        {step === 'register_password' && (
          <TextField
            fullWidth
            type={showPassword ? "text" : "password"}
            label="Contraseña"
            name="password"
            value={formData.password}
            onChange={handleChange}
            variant="outlined"
            error={!!error}
            helperText={error}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}

        {step === 'register_recovery' && (
          <TextField
            fullWidth
            label="Dirección de correo electrónico actual"
            name="recovery_email"
            value={formData.recovery_email}
            onChange={handleChange}
            variant="outlined"
            error={!!error}
            helperText={error || "Lo usaremos para mantener la seguridad de tu cuenta"}
          />
        )}

        {step === 'register_verify' && (
          <TextField
            fullWidth
            label="Código de verificación"
            name="code"
            value={formData.code}
            onChange={handleChange}
            variant="outlined"
            error={!!error}
            helperText={error}
          />
        )}

        {step === 'register_avatar' && (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ mb: 2, color: '#5f6368' }}>
              Elige una foto para tu perfil. Este paso es obligatorio.
            </Typography>
            {avatarPreview ? (
              <Box>
                <Avatar src={avatarPreview} sx={{ width: 130, height: 130, mx: 'auto', mb: 1, border: '3px solid #e0e0e0' }} />
                <Button size="small" onClick={() => { setAvatarFile(null); setAvatarPreview(""); setError(""); }} sx={{ textTransform: 'none' }}>
                  Cambiar foto
                </Button>
              </Box>
            ) : (
              <Button variant="outlined" component="label" sx={{ px: 4, py: 3, borderStyle: 'dashed' }}>
                <PhotoCamera sx={{ mr: 1 }} />
                Seleccionar foto
                <input hidden type="file" accept="image/*" onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setAvatarFile(file);
                    setAvatarPreview(URL.createObjectURL(file));
                    setError("");
                  }
                }} />
              </Button>
            )}
            {error && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                {error}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Acciones */}
      <Box sx={{ width: "100%", display: "flex", justifyContent: step === 'register_success' ? "flex-end" : "space-between", alignItems: "center" }}>
        {step !== 'register_success' && (
          <>
            {step === 'login_password' || step === 'forgot_password' || step === 'forgot_password_success' ? (
              <Button onClick={() => setStep('login_email')} sx={{ textTransform: "none", color: "#1a73e8" }}>
                {step === 'forgot_password_success' ? 'Volver al inicio' : 'Atrás'}
              </Button>
            ) : step !== 'login_email' && step !== 'register_name' ? (
              <Button onClick={() => {
                if(step === 'register_username') setStep('register_name');
                if(step === 'register_password') setStep('register_username');
                if(step === 'register_recovery') setStep('register_password');
                if(step === 'register_verify') setStep('register_recovery');
                if(step === 'register_avatar') { setStep('register_verify'); setAvatarFile(null); setAvatarPreview(""); }
              }} sx={{ textTransform: "none", color: "#1a73e8" }}>
                Atrás
              </Button>
            ) : (
              <Button onClick={handleSwitchMode} sx={{ textTransform: "none", color: "#1a73e8" }}>
                {step === 'login_email' ? 'Crear cuenta' : 'Iniciar sesión'}
              </Button>
            )}
          </>
        )}

        <Button 
          variant="contained" 
          onClick={step === 'register_success' ? () => {
            navigator.clipboard.writeText(`Usuario: ${formData.hitpoly_username}\nContraseña: ${formData.password}`);
            handleNextStep();
          } : handleNextStep}
          disabled={loading || step === 'forgot_password_success'}
          sx={{ 
            bgcolor: "#1a73e8", 
            "&:hover": { bgcolor: "#1557b0" },
            textTransform: "none",
            px: 3,
            py: 1,
            display: step === 'forgot_password_success' ? 'none' : 'inline-flex'
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : step === 'register_success' ? 'Copiar y Continuar' : step === 'forgot_password' ? 'Enviar enlace' : step === 'register_avatar' ? 'Subir foto y continuar' : 'Siguiente'}
        </Button>
      </Box>
    </Box>
  );
};

export default GoogleAuthModal;
